// Line Entry — counting at the dock on a handheld (canvas board 9).
//
// The compact (mobile) form of a line form with quick add: how many lines
// are counted, a big scan field (and the camera), the line just scanned with
// −1 / +1 / +10 / = expected, and what is still to count. Every change goes
// through the same quick-add path the desktop grid uses.

import { useMemo, useState } from 'react'
import { Camera, ScanLine } from 'lucide-react'
import { ScanCapture } from '../../scan/ScanCapture'
import { evaluateLineLimits, type ResolvedLineLimit } from './model'
import type { QuickAddController } from './QuickAdd'
import { IDLE_TONE, STATUS_TONE, T } from './styles'

interface Props {
  qa: QuickAddController
  rows: Array<Record<string, unknown>>
  rowKeyOf: (r: Record<string, unknown>) => string
  keys: string[]
  qty: string | null
  labelOf: (col: string) => string
  /** The column after the key — a line's name. */
  nameOf: (r: Record<string, unknown>) => string
  onSetCell: (rowKey: string, col: string, value: unknown) => void
}

const tap: React.CSSProperties = {
  height: 48, minWidth: 48, borderRadius: 10, border: '1px solid #c9d2dc', background: T.bg, color: T.text,
  fontWeight: 600, fontSize: 16, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', gap: 6, padding: '0 14px', fontFamily: 'inherit', flex: 1,
}

export default function HandheldCount({ qa, rows, rowKeyOf, keys, qty, labelOf, nameOf, onSetCell }: Props) {
  const [text, setText] = useState('')
  const [camera, setCamera] = useState(false)
  const live = rows.filter(r => !r._deleted)
  const num = (r: Record<string, unknown>) => (qty ? Number(r[qty] || 0) : 0)
  const counted = live.filter(r => num(r) > 0).length
  const pct = live.length ? Math.round((counted / live.length) * 100) : 0
  // The line the last scan touched (the log names it by code).
  const current = useMemo(() => {
    const last = qa.log.find(e => e.kind === 'added' || e.kind === 'new')
    if (!last) return null
    const code = last.code.toLowerCase()
    return live.find(r => keys.some(k => String(r[k] ?? '').toLowerCase() === code)) || null
  }, [qa.log, live, keys])
  const todo = live.filter(r => num(r) === 0).slice(0, 5)

  const submit = (v: string) => {
    if (!v.trim()) return
    void qa.scan(v)
    setText('')
  }
  const verdict = current ? evaluateLineLimits(current, current._limits as Record<string, ResolvedLineLimit> | undefined, labelOf) : null
  const expected = current && qty ? (current._limits as Record<string, ResolvedLineLimit> | undefined)?.[qty]?.max : undefined
  const tone = verdict?.status ? STATUS_TONE[verdict.status.index] : IDLE_TONE
  const step = (d: number) => {
    if (!current || !qty) return
    onSetCell(rowKeyOf(current), qty, Math.max(0, num(current) + d))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, background: '#f4f6f9' }}>
      <div>
        <div style={{ display: 'flex', fontSize: 14, color: T.muted }}>
          <span><b style={{ color: T.text }}>{counted.toLocaleString()}</b> of {live.length.toLocaleString()} lines counted</span>
          <span style={{ flex: 1 }} />
          <span>{pct}%</span>
        </div>
        <div aria-hidden="true" style={{ height: 6, borderRadius: 3, background: '#dde3ea', marginTop: 6, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: T.accent }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, height: 52, border: `2px solid ${T.accent}`,
          borderRadius: 12, padding: '0 12px', background: T.bg, boxSizing: 'border-box' }}>
          <ScanLine size={22} color="#0061D0" aria-hidden="true" />
          <input aria-label="Scan item" placeholder="Scan or type code" value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(text) } }}
            style={{ border: 0, outline: 0, flex: 1, minWidth: 0, fontFamily: T.mono, fontSize: 17, background: 'transparent', color: T.text }} />
        </label>
        <button type="button" aria-label="Scan with the camera" onClick={() => setCamera(true)}
          style={{ ...tap, flex: '0 0 52px', height: 52, background: T.accent, color: '#fff', border: 0 }}>
          <Camera size={22} />
        </button>
      </div>

      {current && (
        <section aria-label="Current item" style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 14,
          padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: T.muted }}>
                {keys.map(k => String(current[k] ?? '')).filter(Boolean).join(' · ')}
              </span>
              <b style={{ fontSize: 18 }}>{nameOf(current)}</b>
            </div>
            {verdict?.status && (
              <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: tone.bg, color: tone.fg }}>
                {verdict.status.label}{verdict.status.delta != null ? ` ${Math.abs(verdict.status.delta).toLocaleString()}` : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, color: T.muted }}>Counted</span>
              <div style={{ fontFamily: T.mono, fontSize: 29, fontWeight: 600 }}>{num(current).toLocaleString()}</div>
            </div>
            {expected != null && (
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, color: T.muted }}>Expected</span>
                <div style={{ fontFamily: T.mono, fontSize: 29, fontWeight: 600, color: T.muted }}>{Number(expected).toLocaleString()}</div>
              </div>
            )}
          </div>
          {qty && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={tap} onClick={() => step(-1)} aria-label="One less">−1</button>
              <button type="button" style={tap} onClick={() => step(1)} aria-label="One more">+1</button>
              <button type="button" style={tap} onClick={() => step(10)}>+10</button>
              {expected != null && (
                <button type="button" style={tap} aria-label="Set to expected"
                  onClick={() => onSetCell(rowKeyOf(current), qty, expected)}>= {Number(expected).toLocaleString()}</button>
              )}
            </div>
          )}
        </section>
      )}

      {todo.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>Still to count</div>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {todo.map(r => {
              const max = qty ? (r._limits as Record<string, ResolvedLineLimit> | undefined)?.[qty]?.max : undefined
              return (
                <div key={rowKeyOf(r)} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 14px',
                  borderBottom: '1px solid #edf0f4', fontSize: 15 }}>
                  <span style={{ fontFamily: T.mono, fontSize: 13, color: T.muted }}>{keys.map(k => String(r[k] ?? '')).find(Boolean)}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(r)}</span>
                  {max != null && <span style={{ fontFamily: T.mono }}>{Number(max).toLocaleString()}</span>}
                </div>
              )
            })}
          </div>
        </>
      )}

      {qa.log[0] && (qa.log[0].kind === 'missing' || qa.log[0].kind === 'error') && (
        <div role="alert" style={{ fontSize: 14, color: '#a61b1b' }}>
          <span style={{ fontFamily: T.mono }}>{qa.log[0].title}</span> — {qa.log[0].detail}
        </div>
      )}

      {camera && (
        <ScanCapture onScan={(code) => { setCamera(false); submit(code) }} onClose={() => setCamera(false)} />
      )}
    </div>
  )
}
